import Fuse from 'fuse.js';
import type { 
  MenuItem, 
  MenuSection,
  DietType,
  AllergenInfo,
  SpiceLevel,
  CuisineTag
} from '@/types';

// Search and filter types
export interface MenuSearchState {
  query: string;
  isSearching: boolean;
  results: MenuItem[];
  resultCount: number;
  searchHistory: string[];
}

export interface MenuFilterState {
  // Basic filters
  dietTypes: DietType[];
  priceRange: { min: number; max: number } | null;
  sections: string[];
  hasImage: boolean | null;

  // Enhanced diet type filters (from Chat 8)
  allergens: string[];
  spiceLevels: SpiceLevel['level'][];
  cuisineTags: CuisineTag['type'][];
  customTags: string[];

  // Additional filters
  isSignatureDish: boolean | null;
  isChefSpecial: boolean | null;
  preparationTimeRange: { min: number; max: number } | null;
}

export interface SortOptions {
  sortBy: 'name' | 'price' | 'date-added' | 'popularity' | 'section-order';
  sortOrder: 'asc' | 'desc';
}

export interface SearchAndFilterState {
  search: MenuSearchState;
  filters: MenuFilterState;
  sort: SortOptions;
  isFilterPanelOpen: boolean;
  activeFiltersCount: number;
  filteredResults: MenuItem[];
}

// Search configuration
export interface SearchConfig {
  threshold: number; // Fuse.js similarity threshold (0.0 = exact, 1.0 = anything)
  keys: string[]; // Fields to search in
  includeScore: boolean;
  includeMatches: boolean;
  minMatchCharLength: number;
  shouldSort: boolean;
}

// Filter utilities
export interface FilterOptions {
  searchQuery?: string;
  filters?: Partial<MenuFilterState>;
  sort?: SortOptions;
}

export interface FilterResult {
  items: MenuItem[];
  count: number;
  appliedFilters: string[];
}

// Default search configuration optimized for menu items
export const DEFAULT_SEARCH_CONFIG: SearchConfig = {
  threshold: 0.3, // Allow some typos but not too fuzzy
  keys: [
    'name',
    'description', 
    'ingredients',
    'allergens',
    'enhancedAllergens.custom',
    'enhancedAllergens.mayContain',
    'cuisineTags.region',
    'cuisineTags.subType',
    'customTags.name'
  ],
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 2,
  shouldSort: true,
};

// Create Fuse instance for menu item searching
export const createMenuSearchEngine = (items: MenuItem[], config: SearchConfig = DEFAULT_SEARCH_CONFIG) => {
  return new Fuse(items, config);
};

// Search menu items with fuzzy matching
export const searchMenuItems = (
  items: MenuItem[],
  query: string,
  config: SearchConfig = DEFAULT_SEARCH_CONFIG
): MenuItem[] => {
  if (!query || query.trim().length < 2) {
    return items;
  }

  const fuse = createMenuSearchEngine(items, config);
  const searchResults = fuse.search(query.trim());
  
  return searchResults.map(result => result.item);
};

// Get available filter options based on current menu items
export const getAvailableFilterOptions = (items: MenuItem[], sections: MenuSection[]) => {
  const availableOptions = {
    dietTypes: new Set<DietType>(),
    allergens: new Set<string>(),
    spiceLevels: new Set<SpiceLevel['level']>(),
    cuisineTags: new Set<CuisineTag['type']>(),
    customTags: new Set<string>(),
    sections: new Set<string>(),
    priceRange: { min: 0, max: 0 },
    preparationTimeRange: { min: 0, max: 0 },
    hasSignatureDish: false,
    hasChefSpecial: false,
    hasImages: false,
  };

  if (items.length === 0) {
    return availableOptions;
  }

  // Calculate price range
  const prices = items.map(item => item.price).filter(Boolean);
  if (prices.length > 0) {
    availableOptions.priceRange = {
      min: Math.floor(Math.min(...prices)),
      max: Math.ceil(Math.max(...prices))
    };
  }

  // Calculate preparation time range
  const prepTimes = items.map(item => item.preparationTime).filter(Boolean) as number[];
  if (prepTimes.length > 0) {
    availableOptions.preparationTimeRange = {
      min: Math.floor(Math.min(...prepTimes)),
      max: Math.ceil(Math.max(...prepTimes))
    };
  }

  items.forEach(item => {
    // Diet types
    availableOptions.dietTypes.add(item.dietType);

    // Enhanced allergens
    if (item.enhancedAllergens) {
      // Standard allergens
      Object.entries(item.enhancedAllergens).forEach(([key, value]) => {
        if (key !== 'custom' && key !== 'mayContain' && value === true) {
          availableOptions.allergens.add(key);
        }
      });
      
      // Custom allergens
      item.enhancedAllergens.custom?.forEach(allergen => {
        availableOptions.allergens.add(allergen);
      });
      
      // May contain allergens
      item.enhancedAllergens.mayContain?.forEach(allergen => {
        availableOptions.allergens.add(allergen);
      });
    }

    // Fallback to basic allergens
    if (!item.enhancedAllergens && item.allergens) {
      item.allergens.forEach(allergen => {
        availableOptions.allergens.add(allergen);
      });
    }

    // Enhanced spice levels
    if (item.enhancedSpiceLevel?.level) {
      availableOptions.spiceLevels.add(item.enhancedSpiceLevel.level);
    } else if (item.spiceLevel) {
      // Map old numeric spice levels to new system
      const spiceLevelMap = {
        1: 'mild',
        2: 'mild', 
        3: 'medium',
        4: 'hot',
        5: 'extra-hot'
      } as const;
      availableOptions.spiceLevels.add(spiceLevelMap[item.spiceLevel as keyof typeof spiceLevelMap]);
    }

    // Cuisine tags
    item.cuisineTags?.forEach(tag => {
      availableOptions.cuisineTags.add(tag.type);
    });

    // Custom tags
    item.customTags?.forEach(tag => {
      availableOptions.customTags.add(tag.name);
    });

    // Special dishes
    if (item.isSignatureDish) {
      availableOptions.hasSignatureDish = true;
    }
    if (item.isChefSpecial) {
      availableOptions.hasChefSpecial = true;
    }

    // Images
    if (item.imageUrl) {
      availableOptions.hasImages = true;
    }
  });

  // Add sections
  sections.forEach(section => {
    availableOptions.sections.add(section.id);
  });

  return availableOptions;
};

// Apply filters to menu items
export const applyFilters = (
  items: MenuItem[],
  filters: Partial<MenuFilterState>,
  sections: MenuSection[]
): FilterResult => {
  let filteredItems = [...items];
  const appliedFilters: string[] = [];

  // Diet type filter
  if (filters.dietTypes && filters.dietTypes.length > 0) {
    filteredItems = filteredItems.filter(item => {
      const matchesDietType = filters.dietTypes!.includes(item.dietType);
      const matchesVegan = filters.dietTypes!.includes('vegan' as DietType) && item.isVegan;
      const matchesJain = filters.dietTypes!.includes('jain' as DietType) && item.isJain;
      return matchesDietType || matchesVegan || matchesJain;
    });
    appliedFilters.push(`Diet: ${filters.dietTypes.join(', ')}`);
  }

  // Price range filter
  if (filters.priceRange) {
    filteredItems = filteredItems.filter(item => 
      item.price >= filters.priceRange!.min && item.price <= filters.priceRange!.max
    );
    appliedFilters.push(`Price: ${filters.priceRange.min} - ${filters.priceRange.max}`);
  }

  // Section filter
  if (filters.sections && filters.sections.length > 0) {
    filteredItems = filteredItems.filter(item => 
      filters.sections!.includes(item.sectionId)
    );
    const sectionNames = filters.sections.map(sectionId => 
      sections.find(s => s.id === sectionId)?.name || 'Unknown'
    );
    appliedFilters.push(`Sections: ${sectionNames.join(', ')}`);
  }

  // Image filter
  if (filters.hasImage !== null) {
    filteredItems = filteredItems.filter(item => 
      filters.hasImage ? !!item.imageUrl : !item.imageUrl
    );
    appliedFilters.push(filters.hasImage ? 'With images' : 'Without images');
  }

  // Enhanced allergen filters
  if (filters.allergens && filters.allergens.length > 0) {
    filteredItems = filteredItems.filter(item => {
      if (item.enhancedAllergens) {
        // Check standard allergens
        const hasStandardAllergen = filters.allergens!.some(allergen =>
          item.enhancedAllergens![allergen as keyof AllergenInfo] === true
        );
        
        // Check custom allergens
        const hasCustomAllergen = filters.allergens!.some(allergen =>
          item.enhancedAllergens!.custom?.includes(allergen) ||
          item.enhancedAllergens!.mayContain?.includes(allergen)
        );
        
        return hasStandardAllergen || hasCustomAllergen;
      } else if (item.allergens) {
        // Fallback to basic allergens
        return filters.allergens!.some(allergen => 
          item.allergens!.includes(allergen)
        );
      }
      return false;
    });
    appliedFilters.push(`Allergens: ${filters.allergens.join(', ')}`);
  }

  // Spice level filter
  if (filters.spiceLevels && filters.spiceLevels.length > 0) {
    filteredItems = filteredItems.filter(item => {
      if (item.enhancedSpiceLevel?.level) {
        return filters.spiceLevels!.includes(item.enhancedSpiceLevel.level);
      } else if (item.spiceLevel) {
        // Map old numeric spice levels to new system
        const spiceLevelMap = {
          1: 'mild',
          2: 'mild', 
          3: 'medium',
          4: 'hot',
          5: 'extra-hot'
        } as const;
        const mappedLevel = spiceLevelMap[item.spiceLevel as keyof typeof spiceLevelMap];
        return filters.spiceLevels!.includes(mappedLevel);
      }
      return false;
    });
    appliedFilters.push(`Spice: ${filters.spiceLevels.join(', ')}`);
  }

  // Cuisine tag filter
  if (filters.cuisineTags && filters.cuisineTags.length > 0) {
    filteredItems = filteredItems.filter(item =>
      item.cuisineTags?.some(tag => 
        filters.cuisineTags!.includes(tag.type)
      )
    );
    appliedFilters.push(`Cuisine: ${filters.cuisineTags.join(', ')}`);
  }

  // Custom tag filter
  if (filters.customTags && filters.customTags.length > 0) {
    filteredItems = filteredItems.filter(item =>
      item.customTags?.some(tag => 
        filters.customTags!.includes(tag.name)
      )
    );
    appliedFilters.push(`Tags: ${filters.customTags.join(', ')}`);
  }

  // Signature dish filter
  if (filters.isSignatureDish !== null) {
    filteredItems = filteredItems.filter(item => 
      !!item.isSignatureDish === filters.isSignatureDish
    );
    appliedFilters.push(filters.isSignatureDish ? 'Signature dishes' : 'Non-signature dishes');
  }

  // Chef special filter
  if (filters.isChefSpecial !== null) {
    filteredItems = filteredItems.filter(item => 
      !!item.isChefSpecial === filters.isChefSpecial
    );
    appliedFilters.push(filters.isChefSpecial ? "Chef's specials" : "Non-chef specials");
  }

  // Preparation time filter
  if (filters.preparationTimeRange) {
    filteredItems = filteredItems.filter(item => 
      item.preparationTime && 
      item.preparationTime >= filters.preparationTimeRange!.min && 
      item.preparationTime <= filters.preparationTimeRange!.max
    );
    appliedFilters.push(`Prep time: ${filters.preparationTimeRange.min}-${filters.preparationTimeRange.max} min`);
  }

  return {
    items: filteredItems,
    count: filteredItems.length,
    appliedFilters
  };
};

// Apply sorting to menu items
export const applySorting = (items: MenuItem[], sort: SortOptions, sections: MenuSection[]): MenuItem[] => {
  const sortedItems = [...items];
  
  switch (sort.sortBy) {
    case 'name':
      sortedItems.sort((a, b) => {
        const comparison = a.name.localeCompare(b.name);
        return sort.sortOrder === 'asc' ? comparison : -comparison;
      });
      break;
      
    case 'price':
      sortedItems.sort((a, b) => {
        const comparison = a.price - b.price;
        return sort.sortOrder === 'asc' ? comparison : -comparison;
      });
      break;
      
    case 'date-added':
      sortedItems.sort((a, b) => {
        const comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        return sort.sortOrder === 'asc' ? comparison : -comparison;
      });
      break;
      
    case 'popularity':
      sortedItems.sort((a, b) => {
        // Prioritize popular items, then signature, then chef special
        const aScore = (a.isPopular ? 3 : 0) + (a.isSignatureDish ? 2 : 0) + (a.isChefSpecial ? 1 : 0);
        const bScore = (b.isPopular ? 3 : 0) + (b.isSignatureDish ? 2 : 0) + (b.isChefSpecial ? 1 : 0);
        const comparison = bScore - aScore; // Higher score first
        return sort.sortOrder === 'asc' ? -comparison : comparison;
      });
      break;
      
    case 'section-order':
      sortedItems.sort((a, b) => {
        const sectionA = sections.find(s => s.id === a.sectionId);
        const sectionB = sections.find(s => s.id === b.sectionId);
        
        if (sectionA && sectionB) {
          if (sectionA.order !== sectionB.order) {
            const sectionComparison = sectionA.order - sectionB.order;
            return sort.sortOrder === 'asc' ? sectionComparison : -sectionComparison;
          }
        }
        
        // If same section or no section info, sort by item order
        const comparison = a.order - b.order;
        return sort.sortOrder === 'asc' ? comparison : -comparison;
      });
      break;
      
    default:
      // Default to section order
      return applySorting(items, { sortBy: 'section-order', sortOrder: 'asc' }, sections);
  }
  
  return sortedItems;
};

// Default empty states
export const createEmptySearchState = (): MenuSearchState => ({
  query: '',
  isSearching: false,
  results: [],
  resultCount: 0,
  searchHistory: [],
});

export const createEmptyFilterState = (): MenuFilterState => ({
  dietTypes: [],
  priceRange: null,
  sections: [],
  hasImage: null,
  allergens: [],
  spiceLevels: [],
  cuisineTags: [],
  customTags: [],
  isSignatureDish: null,
  isChefSpecial: null,
  preparationTimeRange: null,
});

export const createDefaultSortOptions = (): SortOptions => ({
  sortBy: 'section-order',
  sortOrder: 'asc',
});

// Utility to count active filters
export const countActiveFilters = (filters: Partial<MenuFilterState>): number => {
  let count = 0;
  
  if (filters.dietTypes && filters.dietTypes.length > 0) count++;
  if (filters.priceRange) count++;
  if (filters.sections && filters.sections.length > 0) count++;
  if (filters.hasImage !== null) count++;
  if (filters.allergens && filters.allergens.length > 0) count++;
  if (filters.spiceLevels && filters.spiceLevels.length > 0) count++;
  if (filters.cuisineTags && filters.cuisineTags.length > 0) count++;
  if (filters.customTags && filters.customTags.length > 0) count++;
  if (filters.isSignatureDish !== null) count++;
  if (filters.isChefSpecial !== null) count++;
  if (filters.preparationTimeRange) count++;
  
  return count;
};

// Clear all filters
export const clearAllFilters = (): MenuFilterState => createEmptyFilterState();

// Search with filters and sorting combined
export const searchAndFilter = (
  items: MenuItem[],
  sections: MenuSection[],
  options: FilterOptions
): FilterResult => {
  let processedItems = [...items];
  
  // Apply search query first
  if (options.searchQuery) {
    processedItems = searchMenuItems(processedItems, options.searchQuery);
  }
  
  // Apply filters
  const filterResult = applyFilters(processedItems, options.filters || {}, sections);
  
  // Apply sorting
  if (options.sort) {
    filterResult.items = applySorting(filterResult.items, options.sort, sections);
  }
  
  return filterResult;
};
